import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchCameraDetails } from '../../services/deviceService';
import './CameraDetail.css';
import { formatFriendlyTimestamp } from '../../utils/helpers';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../shared/LoadingSpinner';

function CameraDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [camera, setCamera] = useState(null);
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { url } = location.state || {};
  const [viewMode, setViewMode] = useState('live');
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const isDateRangeValid = new Date(startDate) <= new Date(endDate);
  const navigate = useNavigate();
  const filteredAlerts = camera?.alerts?.filter((alert) => {
    const alertDate = new Date(alert.start_time).toISOString().split('T')[0];
    return (
      (!startDate || alertDate >= startDate) &&
      (!endDate || alertDate <= endDate)
    );
  }) || [];
  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchCameraDetails(id);
        setCamera(data);
      } catch (err) {
        console.error('Camera fetch failed:', err);
      }
    }

    loadData();
  }, [id], [url]);

  if (!camera) return <LoadingSpinner message={t("loading.cameraFeed")} />;

  const {
    name,
    camera_type,
    is_active,
  } = camera;

  const getStreamUrl = () => {
    const annotate = viewMode === 'annotated' ? 'True' : 'False';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}annotate=${annotate}&t=${Date.now()}`;
  };
  const handleClick = (alertId) => {
    window.open(`/alerts/${alertId}`, '_self', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="camera-grid-wrapper">
        <div className="top-nav-bar-camera">
          <div className="nav-right">
            <button className="dashboard-button" onClick={() => navigate('/dashboard')}>
              {t("button.dashboard")}
            </button>
          </div>
        </div>
      </div>
      <div className="camera-detail-layout">
        <div className="camera-left-panel">
          <h2>{name} <span className="camera-type">({camera_type})</span></h2>
          <p>{t("label.statusColon")} <strong className={is_active ? 'status-active' : 'status-inactive'}>
            {is_active ? t("status.active") : t("status.inactive")}
          </strong></p>
          <div className="view-mode-toggle">
            <button
              className={viewMode === 'live' ? 'active' : ''}
              onClick={() => setViewMode('live')}
            >
              {t("camera.viewModeLive")}
            </button>
            <button
              className={viewMode === 'annotated' ? 'active' : ''}
              onClick={() => setViewMode('annotated')}
            >
              {t("camera.viewModeAnnotated")}
            </button>
          </div>
          <img
            src={getStreamUrl()}
            alt="MJPEG Stream"
            className="stream-image"
          />
        </div>
        <aside className="camera-right-panel">
          <div className="alerts-sorting">
            <div className="sort-group">
              <label>{t("label.dateRange")}</label>
              <div className="date-range-row">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span style={{ margin: '0 8px' }}>{t("label.to")}</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate === today ? today : startDate}
                  disabled={!startDate}
                />
              </div>
              {!isDateRangeValid && (
                <p className="date-warning">{t("label.dateWarning")}</p>
              )}
            </div>
          </div>

          <ul className="alert-list">
            {filteredAlerts.length === 0 ? (
              <p className="no-alerts-message">{t("label.noAlertsDate")}</p>
            ) : (
              filteredAlerts.map((alert) => (
                <li key={alert.id} className={`alert-card severity-${alert.severity.toLowerCase()}`}>
                  <video
                    src={alert.video_url ? `${BASE_URL}${alert.video_url}` : alert.video_url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="alert-thumbnails"
                    onClick={() => handleClick(alert.id)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="alert-meta">
                    <div className="alert-id" title={alert.id}>
                      {alert.id.slice(0, 15) + '...'}
                    </div>

                    <div className="alert-status">
                      <strong>Status:</strong> {alert.alert_analysis}
                    </div>

                    <div
                      className="alert-message"
                      title={alert.message}
                      onClick={() => handleClick(alert.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {alert.message.split(' ').slice(0, 4).join(' ') + '...'}
                    </div>

                    <div className="alert-severity-block">
                      <div className="alert-severity">{alert.severity.toUpperCase()}</div>
                      <div className="alert-time">{formatFriendlyTimestamp(alert.start_time)}</div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </aside>
      </div>
    </>
  );
}
export default CameraDetail;